import React, { useEffect, useMemo, useState } from 'react';
import { Box, FormLabel, Input, keyframes } from '@chakra-ui/react';

const caretBlink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

/**
 * Search input with a typewriter-style rotating placeholder so staff
 * can see what fields are searchable.
 *
 * Animation pauses while focused or when the field has a value.
 */
export function AnimatedSearchInput({
  label = 'Search',
  hints = [],
  value = '',
  onChange,
  h = '40px',
  fontSize = '14px',
  labelFontSize = '13px',
  isDisabled,
  ...props
}) {
  const phrases = useMemo(
    () => (Array.isArray(hints) ? hints.filter(Boolean) : []).map(String),
    [hints],
  );

  const [phraseIndex, setPhraseIndex] = useState(0);
  const [display, setDisplay] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [focused, setFocused] = useState(false);

  const paused = focused || Boolean(String(value || '').trim()) || !phrases.length;

  useEffect(() => {
    if (paused) return undefined;

    const full = phrases[phraseIndex % phrases.length] || '';
    const typingMs = deleting ? 28 : 48;
    const holdMs = 1400;

    if (!deleting && display === full) {
      const t = setTimeout(() => setDeleting(true), holdMs);
      return () => clearTimeout(t);
    }

    if (deleting && display === '') {
      const t = setTimeout(() => {
        setDeleting(false);
        setPhraseIndex((i) => (i + 1) % phrases.length);
      }, 280);
      return () => clearTimeout(t);
    }

    const t = setTimeout(() => {
      const nextLen = display.length + (deleting ? -1 : 1);
      setDisplay(full.slice(0, Math.max(0, nextLen)));
    }, typingMs);

    return () => clearTimeout(t);
  }, [paused, deleting, display, phraseIndex, phrases]);

  useEffect(() => {
    // Reset when hint list changes (e.g. page switch).
    setPhraseIndex(0);
    setDisplay('');
    setDeleting(false);
  }, [phrases]);

  const showCaret = !paused && phrases.length > 0;

  return (
    <Box>
      {label ? (
        <FormLabel fontSize={labelFontSize} mb={2}>
          {label}
        </FormLabel>
      ) : null}
      <Box position="relative">
        <Input
          h={h}
          fontSize={fontSize}
          value={value}
          onChange={onChange}
          placeholder=""
          isDisabled={isDisabled}
          onFocus={(e) => {
            setFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            props.onBlur?.(e);
          }}
          {...props}
        />
        {!String(value || '').trim() && !focused && phrases.length ? (
          <Box
            position="absolute"
            left="16px"
            right="16px"
            top="50%"
            transform="translateY(-50%)"
            pointerEvents="none"
            fontSize={fontSize}
            color="text.secondary"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            display="flex"
            alignItems="center"
          >
            <Box as="span">{display}</Box>
            {showCaret ? (
              <Box
                as="span"
                display="inline-block"
                w="1.5px"
                h="1em"
                ml="1px"
                bg="text.secondary"
                animation={`${caretBlink} 1s step-end infinite`}
                flexShrink={0}
              />
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
